<template>
  <v-card>
    <v-card-title>
      <div style="cursor: pointer" @click="goToServiceTypesTable">Typy služeb</div>
      <div style="margin-right: 15px; margin-left: 15px">--></div>
      {{ data.serviceName }}
    </v-card-title>
    <v-form ref="serviceTypeForm" @submit.prevent="updateServiceTypeData">
      <v-card-text>
        <!-- Basic Information Section -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">Základní informace</h3>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <v-text-field disabled hide-details="auto" label="ID" v-model="data._id" outlined filled dense />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <v-text-field hide-details="auto" disabled label="Klíč služby" v-model="data.serviceName" outlined filled dense />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <v-text-field 
              hide-details="auto" 
              type="number" 
              label="Výchozí cena (Kč)" 
              v-model.number="data.defaultPrice" 
              outlined 
              filled 
              dense 
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <v-select
                hide-details="auto"
                v-model="data.unit"
                :items="unitOptions"
                label="Jednotka"
                outlined
                filled
                dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <v-select
                hide-details="auto"
                v-model="data.locations"
                :items="locationOptions"
                label="Lokality"
                outlined
                filled
                dense
                multiple
                chips
                small-chips
                deletable-chips
                clearable
            />
          </v-col>
        </v-row>

        <!-- Estimate Configuration Section -->
        <v-row class="mt-6">
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="mb-3">Konfigurace odhadů</h3>
          </v-col>
        </v-row>

        <v-row v-if="!data.estimateConfiguration || data.estimateConfiguration.length === 0">
          <v-col cols="12">
            <v-alert type="info" text dense>
              Zatím nejsou definovány žádné konfigurace. Přidejte první konfiguraci pomocí tlačítka níže.
            </v-alert>
          </v-col>
        </v-row>

        <v-row v-for="(config, index) in data.estimateConfiguration" :key="index" class="config-row">
          <v-col cols="12">
            <v-card outlined class="pa-3 mb-2">
              <v-row>
                <v-col cols="12" sm="3">
                  <v-text-field 
                    hide-details="auto" 
                    label="Klíč" 
                    v-model="config.key" 
                    outlined 
                    dense 
                    placeholder="např. small, medium, large"
                  />
                </v-col>
                <v-col cols="12" sm="5">
                  <v-text-field 
                    hide-details="auto" 
                    label="Popis" 
                    v-model="config.description" 
                    outlined 
                    dense 
                    placeholder="např. Do 20m²"
                  />
                </v-col>
                <v-col cols="6" sm="2">
                  <v-text-field 
                    hide-details="auto" 
                    type="number"
                    label="Cena (Kč)" 
                    v-model.number="config.price" 
                    outlined 
                    dense 
                  />
                </v-col>
                <v-col cols="6" sm="2">
                  <v-text-field 
                    hide-details="auto" 
                    type="number"
                    label="Trvání (h)" 
                    v-model.number="config.duration" 
                    outlined 
                    dense 
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  <v-btn 
                    small 
                    color="error" 
                    outlined 
                    @click="removeConfig(index)"
                  >
                    Odstranit
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-btn 
              small 
              color="secondary" 
              outlined 
              @click="addNewConfig"
            >
              <v-icon small left>mdi-plus</v-icon>
              Přidat konfiguraci
            </v-btn>
          </v-col>
        </v-row>

        <!-- Discount Configuration Section -->
        <v-row class="mt-6">
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="mb-3">Nastavení slev pro tuto službu</h3>
          </v-col>
        </v-row>

        <!-- General Cleaning Intervals -->
        <v-row v-if="data.serviceName === 'general_cleaning'">
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.oneTimeOrderPercent"
              label="Sleva na jednorázový úklid (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.threeMonthsOrderPercent"
              label="Sleva na úklid jednou za 3 měsíce (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.fourMonthsOrderPercent"
              label="Sleva na úklid jednou za 4 měsíce (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.sixMonthsOrderPercent"
              label="Sleva na úklid jednou za 6 měsíců (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
        </v-row>

        <!-- Standard Cleaning Intervals -->
        <v-row v-else>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.oneTimeOrderPercent"
              label="Sleva na jednorázový úklid (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.weeklyOrderPercent"
              label="Sleva na úklid každý týden (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.biweeklyOrderPercent"
              label="Sleva na úklid každé dva týdny (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="discounts.monthlyOrderPercent"
              label="Sleva na úklid každý měsíc (%)"
              type="number"
              outlined
              dense
              hide-details="auto"
              :rules="[v => (v === null || v === undefined || v === '' || (v >= 0 && v <= 100)) || 'Hodnota musí být mezi 0-100']"
            />
          </v-col>
        </v-row>

      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn small color="primary" class="mx-1" type="submit">Uložit</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import _ from "lodash-es"

export default {
  name: "ServiceTypeCard",
  components: {
  },
  props: {
    id: {
      required: false,
      default: () => null
    },
    creating: {
      type: Boolean
    }
  },
  data() {
    return {
      data: {
        estimateConfiguration: [],
        locations: [],
        discounts: {
          oneTimeOrderPercent: 0,
          weeklyOrderPercent: 0,
          biweeklyOrderPercent: 0,
          monthlyOrderPercent: 0
        }
      },
      discounts: {
        oneTimeOrderPercent: 0,
        weeklyOrderPercent: 0,
        biweeklyOrderPercent: 0,
        monthlyOrderPercent: 0,
        threeMonthsOrderPercent: 0,
        fourMonthsOrderPercent: 0,
        sixMonthsOrderPercent: 0,
      },
      copyData: {},
      saveModel: {},
      unitOptions: [
        { text: 'Hodina', value: 'hour' },
        { text: 'Plocha', value: 'area' },
        { text: 'Zákazník', value: 'customer' }
      ],
      locationOptions: []
    }
  },
  mounted() {
    this.getData()
    this.getLocations()
  },
  methods: {
    getData() {
      this.axios.get('/v1/service-types/' + this.id)
          .then(({data}) => {
            this.data = data
            if (!this.data.estimateConfiguration) {
              this.data.estimateConfiguration = []
            }
            if (!this.data.locations) {
              this.data.locations = []
            }
            if (!this.data.discounts) {
              this.data.discounts = {
                oneTimeOrderPercent: 0,
                weeklyOrderPercent: 0,
                biweeklyOrderPercent: 0,
                monthlyOrderPercent: 0
              }
            }
            // Initialize local discounts object
            this.discounts = {
              oneTimeOrderPercent: this.data.discounts?.oneTimeOrderPercent || 0,
              weeklyOrderPercent: this.data.discounts?.weeklyOrderPercent || 0,
              biweeklyOrderPercent: this.data.discounts?.biweeklyOrderPercent || 0,
              monthlyOrderPercent: this.data.discounts?.monthlyOrderPercent || 0,
              threeMonthsOrderPercent: this.data.discounts?.threeMonthsOrderPercent || 0,
              fourMonthsOrderPercent: this.data.discounts?.fourMonthsOrderPercent || 0,
              sixMonthsOrderPercent: this.data.discounts?.sixMonthsOrderPercent || 0,
            }
            this.copyData = _.cloneDeep(data)
          }).catch(() => this.$router.go(-1))
    },
    getLocations() {
      this.axios.get('/v1/locations')
          .then(({data}) => {
            this.locationOptions = data.map(location => ({
              text: location.displayName,
              value: location.name
            }))
          })
    },
    getObjectDiff(obj1, obj2) {
      return Object.keys(obj1).reduce((result, key) => {
        if (!obj2.hasOwnProperty(key)) {
          result.push(key);
        } else if (_.isEqual(obj1[key], obj2[key])) {
          const resultKeyIndex = result.indexOf(key);
          result.splice(resultKeyIndex, 1);
        }
        return result;
      }, Object.keys(obj2));
    },
    updateServiceTypeData() {
      // Validate form before submission
      if (!this.$refs.serviceTypeForm.validate()) {
        this.$store.commit('addAlert', {
          type: 'error',
          message: 'Prosím opravte chyby ve formuláři'
        })
        return
      }

      // Update data.discounts with the local discounts values
      this.data.discounts = { ...this.discounts }

      let diffKeys = this.getObjectDiff(this.data, this.copyData)
      let diffData = {}
      diffKeys.forEach(dataKey => {
        diffData[dataKey] = this.data[dataKey]
      })

      // Filter out estimate configurations with empty keys
      if (diffData.estimateConfiguration) {
        diffData.estimateConfiguration = diffData.estimateConfiguration.filter(config => config.key && config.key.trim() !== '')
      }

      this.axios.patch('/v1/service-types/' + this.id, diffData)
          .then(() => {
            this.$store.commit('addAlert', {
              type: 'success',
              message: "Úspěšně uloženo"
            })
            this.copyData = _.cloneDeep(this.data)
          })
          .catch((el) => {
            if (el.response && el.response.data && el.response.data.errors) {
              el.response.data.errors.forEach((error) => {
                this.$store.commit('addAlert', {
                  type: 'error',
                  message: error.message
                })
              })
            } else {
              this.$store.commit('addAlert', {
                type: 'error',
                message: "Chyba při ukládání"
              })
            }
          })
          .finally()
    },
    goToServiceTypesTable() {
      this.$router.push("/app/service-types")
    },
    addNewConfig() {
      if (!this.data.estimateConfiguration) {
        this.data.estimateConfiguration = []
      }
      this.data.estimateConfiguration.push({
        key: '',
        description: '',
        price: 0,
        duration: 0
      })
    },
    removeConfig(index) {
      this.data.estimateConfiguration.splice(index, 1)
    }
  },
  computed: {
  },
  watch: {
    data: {
      deep: true,
      handler: function () {
      }
    }
  }
}
</script>

<style scoped>
.config-row {
  transition: all 0.3s;
}
</style>
